import type {FailedAPITaskCreationAttempt} from '@github-ui/agent-sessions/components/TaskCreationFailedBanner'
import {useCreateAPITask} from '@github-ui/agent-sessions/hooks/use-create-api-task'
import {getAgentTasksQueryKeyForMutation, PAGE_SIZE} from '@github-ui/agent-sessions/hooks/useTasks'
import type {SelectedAgent} from '@github-ui/agent-sessions-types/agent-identity'
import {TraceProvider} from '@github-ui/copilot-chat/components/tracing/TraceProvider'
import {ImmersivePluginsProvider} from '@github-ui/copilot-chat/plugin/registry'
import {CopilotChatProvider} from '@github-ui/copilot-chat/utils/CopilotChatContext'
import {isCopilotPath, isGitHubHomePath} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import type {AgentDropdownRepository, CopilotChatMode} from '@github-ui/copilot-chat-types'
import {NavigableRefProvider} from '@github-ui/navigable-ref'
import type {HandleUserSubmitOptions} from '@github-ui/one-copilot-composer/user-submit'
// eslint-disable-next-line no-restricted-imports
import {ScreenSizeProvider} from '@github-ui/screen-size'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {FailedTasksProvider} from '../contexts/FailedTasksContext'
import {PersonaOnboardingProvider} from '../contexts/PersonaOnboardingContext'
import {PreviewUrlProvider} from '../contexts/PreviewUrlContext'
import {useImmersivePlugins} from '../hooks/use-immersive-plugins'
import {useRouteThreadId} from '../hooks/use-route-thread-id'
import type {CopilotImmersivePayload} from '../routes/payloads'
import {ContentPreview} from './ContentPreview/ContentPreview'
import {
  type ContentPreviewComponentProps,
  ContentPreviewRendererContext,
} from './ContentPreview/ContentPreviewComponentContext'
import {ContentPreviewProvider} from './ContentPreview/ContentPreviewContext'

export function CopilotImmersiveProviders({
  payload,
  children,
  mode = 'immersive',
}: React.PropsWithChildren<{
  payload: CopilotImmersivePayload
  mode?: CopilotChatMode
}>) {
  const threadId = useRouteThreadId()
  const {refs, topic} = useMemo(() => {
    const requestedTopic = payload.requestedTopic

    // Filter out references that point to /copilot itself or the GitHub home/dashboard
    // (github.com, github.com/dashboard) — these are not useful context.
    if (payload.reference?.type === 'github-url') {
      try {
        const {pathname} = new URL(payload.reference.url, window.location.origin)
        if (isCopilotPath(pathname) || isGitHubHomePath(pathname)) {
          return {refs: [], topic: requestedTopic}
        }
      } catch {
        // Invalid URL — fall through to normal handling
      }
    }

    // If there are file diff references in localStorage, don't include the PR reference from the
    // backend URL context. This prevents both the PR and file diff from being attached when
    // a user redirects from PR diff view to immersive chat.
    const hasDiffReferences = copilotLocalStorage.getCopilotDiffReferences()?.length
    if (hasDiffReferences && payload.reference?.type === 'pull-request') {
      return {refs: [], topic: requestedTopic}
    }

    if (payload.reference) {
      return {refs: [payload.reference], topic: requestedTopic}
    } else {
      return {refs: [], topic: requestedTopic}
    }
  }, [payload.requestedTopic, payload.reference])

  const [failedTasks, setFailedTasks] = useState<FailedAPITaskCreationAttempt[]>([])

  const {createTask} = useCreateAPITask({
    queryKey: getAgentTasksQueryKeyForMutation(),
    onError: (error, variables) => {
      setFailedTasks(prev => [
        {
          ...variables,
          errorMessage: error.message,
          timestamp: new Date().toISOString(),
        },
        ...prev,
      ])
    },
    pageSize: PAGE_SIZE,
  })

  const handleUserSubmit = useCallback(
    (
      content: string,
      selectedRepo: AgentDropdownRepository | null,
      selectedBranch: string,
      selectedAgent: SelectedAgent,
      modelId?: string,
      options?: HandleUserSubmitOptions,
    ) => {
      const trimmedContent = content.trim()
      if (trimmedContent === '' || !selectedBranch || selectedBranch.trim() === '') return
      if (selectedRepo) {
        // This mutation will automatically invalidate and refetch the tasks after completion
        createTask({
          nwo: selectedRepo.nameWithOwner,
          problemStatement: trimmedContent,
          baseRef: selectedBranch,
          eventType: 'immersive_page',
          eventContent: trimmedContent,
          model: modelId,
          reasoningEffort: options?.reasoningEffort,
          customAgent: selectedAgent?.subAgentSlug,
          agentId: selectedAgent?.topLevelAgentId,
        })
      }
    },
    [createTask],
  )

  const handleUserSubmitRef = useRef(handleUserSubmit)

  useEffect(() => {
    if (payload.reference?.type === 'repository') {
      copilotLocalStorage.setLastUsedRepository(payload.reference)
    }
  }, [payload.reference])

  useEffect(() => {
    handleUserSubmitRef.current = handleUserSubmit
  }, [handleUserSubmit])

  const isEmbedded = mode === 'assistive'
  const plugins = useImmersivePlugins({
    payload,
    handleUserSubmitRef,
    isEmbedded,
    managedDefaultModelEnabled: true,
  })

  const failedTasksContextValue = useMemo(
    () => ({
      failedTasks,
      setFailedTasks,
      createTask,
    }),
    [failedTasks, createTask],
  )

  const renderContentPreview = useCallback((props: ContentPreviewComponentProps) => <ContentPreview {...props} />, [])

  return (
    <NavigableRefProvider>
      <ScreenSizeProvider>
        <PreviewUrlProvider value={payload.previewUrl}>
          <ImmersivePluginsProvider plugins={plugins}>
            <TraceProvider apiURL={payload.apiURL} ssoOrgs={payload.ssoOrganizations}>
              <CopilotChatProvider
                threadId={threadId}
                refs={refs}
                mode={mode}
                topic={topic}
                ssoOrganizations={payload.ssoOrganizations}
                copilotChatPayload={payload}
              >
                <FailedTasksProvider value={failedTasksContextValue}>
                  <ContentPreviewProvider>
                    <ContentPreviewRendererContext value={renderContentPreview}>
                      <PersonaOnboardingProvider
                        key={payload.currentUserLogin}
                        initialPayload={isEmbedded ? undefined : payload.personaOnboarding}
                      >
                        {children}
                      </PersonaOnboardingProvider>
                    </ContentPreviewRendererContext>
                  </ContentPreviewProvider>
                </FailedTasksProvider>
              </CopilotChatProvider>
            </TraceProvider>
          </ImmersivePluginsProvider>
        </PreviewUrlProvider>
      </ScreenSizeProvider>
    </NavigableRefProvider>
  )
}
